import React from 'react';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import camelcaseKeys from 'camelcase-keys';
import classnames from 'classnames';
import { EagerImg, LazyImg } from 'src/hooks/useLazyLoad';
import { HomeMainWidgetsSkeleton } from 'src/elements/Skeletons/Skeletons';
import { useDeps } from 'src/hooks/useDeps';

import classes from './HomeMainWidgets.scss';

export interface CarouselItem {
    title: string;
    description: string;
    link: string;
    link_text: string;
    image: string;
    image_mobile: string;
}

const homeGalleryWidget = gql`
    query {
        homeGalleryWidget {
            title
            widget_parameters {
                title
                description
                image
                image_mobile
                button_url
                button_label
                status
            }
        }
    }
`;

export const HomeMainWidgets = () => {
    const { loading, data } = useQuery(homeGalleryWidget, {
        // SSR so hero img src is in page source (LCP + SEO).
        ssr: useDeps().env.SSR_GQL,
    });

    const homeGalleryData = camelcaseKeys(data?.homeGalleryWidget, { deep: true });

    if (loading) return <HomeMainWidgetsSkeleton />;

    if (!data || !homeGalleryData) return null;

    const getWidgetData = (index: string) =>
        homeGalleryData.find((data) => data.title.includes(index) && data.widgetParameters.status !== 0);

    const secondWidgetIsImageOnly = getWidgetData('2').title.toUpperCase().includes('IMAGE ONLY');

    const widgetAlt = (index: string) => {
        const params = getWidgetData(index)?.widgetParameters;
        return params?.title || params?.buttonLabel || `Homepage promotion ${index}`;
    };

    return (
        <div
            className={classnames(classes.wrapper, {
                [classes.marginTop]: Boolean(getWidgetData('1')?.widgetParameters?.buttonLabel),
            })}
        >
            <a href={getWidgetData('1')?.widgetParameters?.buttonUrl || undefined} className={classes.widgetFirst}>
                <EagerImg
                    src={getWidgetData('1')?.widgetParameters?.image}
                    alt={widgetAlt('1')}
                    className={classes.image}
                />
                <EagerImg
                    className={classes.imageMobile}
                    src={
                        getWidgetData('1')?.widgetParameters?.imageMobile
                            ? getWidgetData('1')?.widgetParameters?.imageMobile
                            : getWidgetData('1')?.widgetParameters?.image
                    }
                    alt={widgetAlt('1')}
                />
            </a>

            <a
                className={classnames({
                    [classes.widgetSecond]: !secondWidgetIsImageOnly,
                    [classes.widgetSecondImageOnly]: secondWidgetIsImageOnly,
                })}
                href={getWidgetData('2')?.widgetParameters?.buttonUrl || undefined}
            >
                {secondWidgetIsImageOnly && (
                    <>
                        <LazyImg
                            className={classes.image}
                            src={getWidgetData('2')?.widgetParameters?.image}
                            alt={widgetAlt('2')}
                        />

                        <LazyImg
                            className={classes.imageMobile}
                            src={getWidgetData('2')?.widgetParameters?.imageMobile}
                            alt={widgetAlt('2')}
                        />
                    </>
                )}
                {!secondWidgetIsImageOnly && (
                    <>
                        {' '}
                        <div className={classes.content}>
                            <LazyImg
                                className={classnames(classes.image, classes.widget2)}
                                src={getWidgetData('2')?.widgetParameters?.image}
                                alt={widgetAlt('2')}
                            />
                            <div className={classes.sideWidgetContainer}>
                                <h3 className={classes.sideWidgetTitle}>
                                    {getWidgetData('2')?.widgetParameters?.title}
                                </h3>
                                <p className={classes.sideWidgetDescription}>
                                    {getWidgetData('2')?.widgetParameters?.description}
                                </p>
                            </div>
                        </div>
                        <div className={classes.sideWidgetLinkContainer}>
                            <span className={classes.sideWidgetLink}>Shop now</span>
                            <img src={require('../../svg/arrow-orange.svg')} alt="" />
                        </div>{' '}
                    </>
                )}
            </a>
            <a className={classes.widgetThird} href={getWidgetData('3')?.widgetParameters?.buttonUrl || undefined}>
                <LazyImg
                    className={classes.image}
                    src={getWidgetData('3')?.widgetParameters?.image}
                    alt={widgetAlt('3')}
                />
                <LazyImg
                    className={classes.imageMobile}
                    src={
                        getWidgetData('3')?.widgetParameters?.imageMobile
                            ? getWidgetData('3')?.widgetParameters?.imageMobile
                            : getWidgetData('3')?.widgetParameters?.image
                    }
                    alt={widgetAlt('3')}
                />
            </a>
            <a className={classes.widgetFourth} href={getWidgetData('4')?.widgetParameters?.buttonUrl || undefined}>
                <LazyImg
                    className={classes.image}
                    src={getWidgetData('4')?.widgetParameters?.image}
                    alt={widgetAlt('4')}
                />
                <LazyImg
                    className={classes.imageMobile}
                    src={
                        getWidgetData('4')?.widgetParameters?.imageMobile
                            ? getWidgetData('4')?.widgetParameters?.imageMobile
                            : getWidgetData('4')?.widgetParameters?.image
                    }
                    alt={widgetAlt('4')}
                />
            </a>
            <a className={classes.widgetFifth} href={getWidgetData('5')?.widgetParameters?.buttonUrl || undefined}>
                <LazyImg
                    className={classes.image}
                    src={getWidgetData('5')?.widgetParameters?.image}
                    alt={widgetAlt('5')}
                />
                <LazyImg
                    className={classes.imageMobile}
                    src={
                        getWidgetData('5')?.widgetParameters?.imageMobile
                            ? getWidgetData('5')?.widgetParameters?.imageMobile
                            : getWidgetData('5')?.widgetParameters?.image
                    }
                    alt={widgetAlt('5')}
                />
                {getWidgetData('5')?.widgetParameters?.title && (
                    <div className={classes.blurContainer}>
                        <img
                            src={
                                getWidgetData('5')?.widgetParameters?.imageMobile
                                    ? getWidgetData('5')?.widgetParameters?.imageMobile
                                    : getWidgetData('5')?.widgetParameters?.image
                            }
                            className={classes.blurImage}
                            alt={widgetAlt('5')}
                        />
                        <h3 className={classes.title}>{getWidgetData('5')?.widgetParameters?.title}</h3>
                        <img src="../../../static/icons/v-right_orange.svg" alt="" />
                    </div>
                )}
            </a>
            <a className={classes.widgetSixth} href={getWidgetData('6')?.widgetParameters?.buttonUrl || undefined}>
                <LazyImg
                    className={classes.image}
                    src={getWidgetData('6')?.widgetParameters?.image}
                    alt={widgetAlt('6')}
                />
                <LazyImg
                    className={classes.imageMobile}
                    src={
                        getWidgetData('6')?.widgetParameters?.imageMobile
                            ? getWidgetData('6')?.widgetParameters?.imageMobile
                            : getWidgetData('6')?.widgetParameters?.image
                    }
                    alt={widgetAlt('6')}
                />
                {getWidgetData('6')?.widgetParameters?.title && (
                    <div className={classes.blurContainer}>
                        <h3 className={classes.title}>{getWidgetData('6')?.widgetParameters?.title}</h3>
                        <img src="../../../static/icons/v-right_orange.svg" alt="" />
                    </div>
                )}
            </a>
        </div>
    );
};
